import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ProjectDetailsResponse,
  ProjectDetails,
  ApiProjectDetailsResponse,
  ProjectCreateUpdatePayload,
  ProjectCreateUpdateApiResponse,
  ProjectCheckNamePayload,
  ProjectCheckNameApiResponse,
} from '../../models/project/project.model';
import { RestfulApiService } from '../restful-api.service';
import { MOCK_PROJECT_DATA } from '../../mocks/project-mock.data';

// Re-export types for convenience
export type { ProjectDetailsResponse, ProjectDetails, ProjectCreateUpdatePayload, ProjectCreateUpdateApiResponse, ProjectCheckNamePayload, ProjectCheckNameApiResponse };

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private useMockData = false;

  constructor(private api: RestfulApiService) {}

  /**
   * Enable mock data mode for development/testing
   */
  enableMockData(): void {
    this.useMockData = true;
    console.log('[ProjectService] Mock data mode enabled');
  }

  /**
   * Disable mock data mode
   */
  disableMockData(): void {
    this.useMockData = false;
  }

  /**
   * Get project details by UUID and language
   * @param uuid - Project UUID
   * @param language - Language code (e.g., 'eng', 'spa', 'fra')
   * @param preview - Preview mode flag (default: false)
   * @param globalSearch - Global search flag (default: false)
   * @returns Observable<ProjectDetailsResponse>
   */
  getProjectDetails(
    uuid: string,
    language: string = 'eng',
    preview: boolean = false,
    globalSearch: boolean = false
  ): Observable<ProjectDetailsResponse> {
    // Return mock data if enabled
    if (this.useMockData) {
      console.log('[ProjectService] Returning mock project data');
      return of({
        success: true,
        data: MOCK_PROJECT_DATA,
        message: 'Mock project data',
      });
    }

    const params = {
      languageCode: language,
      preview: preview.toString(),
      globalSearch: globalSearch.toString(),
    };

    return this.api.get(
      `/v1/project/details/${uuid}`,
      params
    ).pipe(
      map((apiResponse: any) => this.mapApiResponseToProject(apiResponse))
    );
  }

  /**
   * Get project details by UUID (without language parameter)
   * @param uuid - Project UUID
   * @returns Observable<ProjectDetailsResponse>
   */
  getProjectByUuid(uuid: string): Observable<ProjectDetailsResponse> {
    return this.getProjectDetails(uuid, 'eng');
  }

  /**
   * Create or update a project
   * @param payload - ProjectCreateUpdatePayload
   * @returns Observable<ProjectCreateUpdateApiResponse>
   */
  createOrUpdateProject(payload: ProjectCreateUpdatePayload): Observable<ProjectCreateUpdateApiResponse> {
    return this.api.post('/v1/project/create-update', payload) as Observable<ProjectCreateUpdateApiResponse>;
  }

  /**
   * Get project data for editing (raw API response with UUIDs intact)
   * @param uuid - Project UUID
   * @param languageCode - Language code (e.g., 'eng')
   * @returns Observable of raw API response
   */
  getProjectForEdit(uuid: string, languageCode: string): Observable<any> {
    return this.api.get(`/v1/project/${uuid}`, {
      languageCode,
    });
  }

  /**
   * Check if a project name is already in use
   * @param payload - ProjectCheckNamePayload ({ name, uuid })
   * @returns Observable<ProjectCheckNameApiResponse> — response.response is true if name is a duplicate
   */
  checkProjectName(payload: ProjectCheckNamePayload): Observable<ProjectCheckNameApiResponse> {
    return this.api.post('/v1/project/check-name', payload) as Observable<ProjectCheckNameApiResponse>;
  }

  /**
   * Maps the API response to the ProjectDetails model format.
   * The API returns a flat structure with both organization and project fields at the root level.
   * @param apiResponse - The raw API response
   * @returns ProjectDetailsResponse - The mapped response
   */
  private mapApiResponseToProject(apiResponse: ApiProjectDetailsResponse): ProjectDetailsResponse {
    const data = apiResponse.response;

    const projectDetails: ProjectDetails = {
      // Organisation Basic Info
      organisationUuid: data.organisationUuid,
      organisationName: data.organisationName || '',
      address: data.address ? {
        street: data.address.streetName || '',
        city: data.address.city || '',
        state: data.address.stateProvince || '',
        postalCode: data.address.postalCode || '',
        country: data.address.country || '',
      } : undefined,
      organisationWebsite: data.organisationWebsite,
      organisationSocialMediaLinkedin: data.organisationSocialMediaLinkedin,
      organisationSocialMediaTwitter: data.organisationSocialMediaTwitter,
      organisationSocialMediaFacebook: data.organisationSocialMediaFacebook,
      organisationSocialMediaInstagram: data.organisationSocialMediaInstagram,
      organisationCauseName: data.organisationCauseName,
      organisationCategoriesNames: data.organisationCategoriesNames,
      organisationTargetPopulation: data.organisationTargetPopulation,
      organisationInfluencedCountriesNames: data.organisationInfluencedCountriesNames,
      organisationRegions: data.organisationRegions,
      globalInfluence: data.globalInfluence,
      yearFounded: data.yearFounded,

      // Organisation Media
      organisationChampionLandscapeImageDto: data.organisationChampionLandscapeImageDto ? {
        id: data.organisationChampionLandscapeImageDto.uuid,
        url: data.organisationChampionLandscapeImageDto.url,
        alt: data.organisationName || '',
      } : undefined,
      organisationVideoDto: data.organisationVideoDto ? {
        id: data.organisationVideoDto.uuid,
        url: data.organisationVideoDto.url,
        alt: data.organisationName || '',
      } : undefined,
      organisationLogoDto: data.organisationLogoDto ? {
        id: data.organisationLogoDto.uuid,
        url: data.organisationLogoDto.url,
        alt: data.organisationName || '',
      } : undefined,
      organisationTeamMediaVideoDto: data.organisationTeamMediaVideoDto,
      organisationTeamMediaVideoThumbnailDto: data.organisationTeamMediaVideoThumbnailDto,
      organisationTeamMediaPictureDto: data.organisationTeamMediaPictureDto,
      organisationImagesDto: data.organisationImagesDto?.map((img: any) => ({
        id: img.uuid,
        url: img.url,
        alt: data.organisationName || '',
      })) || [],
      organisationCoverImageDto: data.organisationCoverImageDto ? {
        id: data.organisationCoverImageDto.uuid,
        url: data.organisationCoverImageDto.url,
        alt: data.organisationName || '',
      } : undefined,

      // Organisation Content
      executiveSummary: data.executiveSummary,
      visionStatement: data.visionStatement,
      missionStatement: data.missionStatement,
      organisationValues: data.organisationValues,
      operatingBudget: data.operatingBudget,
      programAndActivities: data.programAndActivities,
      history: data.history,

      // Organisation Team
      organisationLeader: data.organisationLeader ? this.mapTeamMember(data.organisationLeader, 'Organization Leader') : undefined,
      executiveTeams: data.executiveTeams?.map((member: any) => this.mapTeamMember(member, 'Executive Team')) || [],
      boardMembers: data.boardMembers?.map((member: any) => this.mapTeamMember(member, 'Board Member')) || [],
      partnerOrganisations: data.partnerOrganisations || [],
      budgetOrganisations: data.budgetOrganisations || [],

      // Organisation Metrics
      numberOfEmployees: data.numberOfEmployees,
      numberOfVolunteers: data.numberOfVolunteers,
      totalOperatingBudget: data.totalOperatingBudget,
      totalOperatingBudgetSymbol: data.totalOperatingBudgetSymbol,
      totalBudgetExpenses: data.totalBudgetExpenses,

      // Organisation Giving & Champions
      organisationGivingLink: data.organisationGivingLink,
      organisationChampionName: data.organisationChampionName,
      organisationChampionCommitmentAmount: data.organisationChampionCommitmentAmount,
      organisationChampionCommitmentCurrencySymbol: data.organisationChampionCommitmentCurrencySymbol,
      organisationChampionAmountAlreadyInvested: data.organisationChampionAmountAlreadyInvested,
      organisationChampionAmountAlreadyInvestedCurrencySymbol: data.organisationChampionAmountAlreadyInvestedCurrencySymbol,
      organisationChampionVideoUrl: data.organisationChampionVideoUrl,
      organisationThumbnailChampionVideo: data.organisationThumbnailChampionVideo,
      organisationChampionHistory: data.organisationChampionHistory,
      organisationChampionQuote: data.organisationChampionQuote,
      organisationChampionImpactStory: data.organisationChampionImpactStory,
      organisationChampionTopRisks: data.organisationChampionTopRisks,
      organisationChampionUuid: data.organisationChampionUuid,
      organisationDiligenceReport: data.organisationDiligenceReport,
      organisationDocuments: data.organisationDocuments?.map((doc: any) => this.mapDocument(doc)) || [],
      organisationLanguages: data.organisationLanguages?.map((lang: any) => ({
        code: lang.code,
        name: lang.name,
        isDefault: lang.primaryLanguage,
      })) || [],

      // Organisation Champions & Badges
      champions: data.champions || [],
      badges: data.badges || [],
      championed: data.championed,

      // Project Basic Info
      projectUuid: data.projectUuid,
      projectName: data.projectName,
      projectNumber: data.projectNumber,
      projectCauseName: data.projectCauseName,
      projectCategoriesNames: data.projectCategoriesNames,
      projectCountriesNames: data.projectCountriesNames,

      // Project Links & Social Media
      website: data.website,
      projectSocialMediaLinkedin: data.projectSocialMediaLinkedin,
      projectSocialMediaTwitter: data.projectSocialMediaTwitter,
      projectSocialMediaFacebook: data.projectSocialMediaFacebook,
      projectSocialMediaInstagram: data.projectSocialMediaInstagram,
      projectOtherSocialMediaLink: data.projectOtherSocialMediaLink,

      // Project Details
      projectTargetPopulation: data.projectTargetPopulation,
      startDate: data.startDate,
      endDate: data.endDate,
      amountRaisedToDate: data.amountRaisedToDate,
      amountRaisedToDateCurrencySymbol: data.amountRaisedToDateCurrencySymbol,
      totalProjectBudget: data.totalProjectBudget,
      totalProjectBudgetCurrencySymbol: data.totalProjectBudgetCurrencySymbol,

      // Project Media
      projectVideoDto: data.projectVideoDto ? {
        id: data.projectVideoDto.uuid,
        url: data.projectVideoDto.url,
        alt: data.projectName || '',
      } : undefined,
      projectImagesDto: data.projectImagesDto?.map((img: any) => ({
        id: img.uuid,
        url: img.url,
        alt: data.projectName || '',
        position: img.position,
        mimeType: img.type,
      })).sort((a: any, b: any) => (a.position || 0) - (b.position || 0)) || [],
      projectCoverImageDto: data.projectCoverImageDto ? {
        id: data.projectCoverImageDto.uuid,
        url: data.projectCoverImageDto.url,
        alt: data.projectName || '',
      } : undefined,
      projectTeamMediaVideo: data.projectTeamMediaVideo,
      projectTeamMediaThumbnailVideo: data.projectTeamMediaThumbnailVideo,
      projectTeamMediaPicture: data.projectTeamMediaPicture,

      // Project Content
      description: data.description,
      problem: data.problem,
      solution: data.solution,
      activities: data.activities,
      teamSummary: data.teamSummary,

      // Project Team
      projectManagers: data.projectManagers?.map((manager: any) => ({
        fullName: manager.fullName,
        profilePictureUrl: manager.profilePictureUrl,
        bio: manager.bio,
        title: manager.title,
        linkedinProfile: manager.linkedinProfile,
      })) || [],
      teamMembers: data.teamMembers?.map((member: any) => this.mapTeamMember(member, 'Team Member')) || [],

      // Project Outcomes
      immediateResults: data.immediateResults,
      shortTermOutcomes: data.shortTermOutcomes,
      longTermImpact: data.longTermImpact,

      // Project Goals & Partners
      projectGoals: data.projectGoals?.map((goal: any) => ({
        uuid: goal.uuid,
        name: goal.name,
        objective: goal.objective,
        number: goal.number,
        position: goal.position,
      })).sort((a: any, b: any) => a.position - b.position) || [],
      projectPartnerOrganisation: data.projectPartnerOrganisation?.map((partner: any) => ({
        uuid: partner.uuid,
        name: partner.name,
        relationshipDescription: partner.relationshipDescription,
        position: partner.position,
        email: partner.email,
      })).sort((a: any, b: any) => a.position - b.position) || [],

      // Project Metrics
      resultsMeasurement: data.resultsMeasurement,
      financialSustainability: data.financialSustainability,
      potentialForGrowth: data.potentialForGrowth,

      // Project Documents & Reports
      projectDiligenceReport: data.projectDiligenceReport,
      projectDocuments: data.projectDocuments?.map((doc: any) => this.mapDocument(doc)) || [],
      projectLanguages: data.projectLanguages?.map((lang: any) => ({
        code: lang.code,
        name: lang.name,
        isDefault: lang.primaryLanguage,
      })) || [],

      // Project Giving & Champions
      projectGivingLink: data.projectGivingLink,
      projectChampionName: data.projectChampionName,
      projectChampionCommitmentAmount: data.projectChampionCommitmentAmount,
      projectChampionCommitmentCurrencySymbol: data.projectChampionCommitmentCurrencySymbol,
      projectChampionAmountAlreadyInvested: data.projectChampionAmountAlreadyInvested,
      projectChampionAmountAlreadyInvestedCurrencySymbol: data.projectChampionAmountAlreadyInvestedCurrencySymbol,
      projectChampionVideoUrl: data.projectChampionVideoUrl,
      projectThumbnailChampionVideo: data.projectThumbnailChampionVideo,
      projectChampionHistory: data.projectChampionHistory,
      projectChampionQuote: data.projectChampionQuote,
      projectChampionImpactStory: data.projectChampionImpactStory,
      projectChampionTopRisks: data.projectChampionTopRisks,
      projectChampionUuid: data.projectChampionUuid,

      // Donors
      donors: data.donors,

      // Watchlist status - backend returns 'addedToWatchlist'
      isWatchlisted: data.addedToWatchlist || data.isWatchlisted || false,
    };

    return {
      success: true,
      data: projectDetails,
      message: 'Project details retrieved successfully',
    };
  }

  /**
   * Maps a team member from the API response
   * @param member - The team member data from API
   * @param distinction - The member's distinction
   * @returns TeamMember object
   */
  private mapTeamMember(member: any, distinction: string): any {
    return {
      id: member.uuid,
      name: member.fullName || '',
      role: member.jobTitle || member.role || member.title || '',
      distinction: distinction,
      bio: member.bio,
      image: member.profilePicture ? {
        id: member.profilePicture.uuid,
        url: member.profilePicture.url,
        alt: member.fullName || '',
      } : undefined,
      email: member.email,
      linkedin: member.linkedinProfile,
    };
  }

  /**
   * Maps a document from the API response
   * @param doc - The document data from API
   * @returns Document object
   */
  private mapDocument(doc: any): any {
    const documentDto = doc.documentDto || doc;
    const filename = documentDto.filename || '';
    const displayName = filename.split('/').pop() || filename;

    return {
      id: documentDto.uuid,
      title: displayName || 'Document',
      type: documentDto.type,
      url: documentDto.url,
      uploadDate: doc.uploadDate || '',
      size: documentDto.size,
    };
  }
}
